import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiRefreshCw } from 'react-icons/fi';

const HOLIDAY_TYPES = ['National', 'Company', 'Regional'];

const HolidayList = () => {
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', date: '', type: 'Company', description: '' });
  const [year, setYear] = useState(new Date().getFullYear());

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const res = await api.get('/holidays', { params: { year } });
      setHolidays(res.data);
    } catch (err) {
      toast.error('Failed to fetch holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHolidays(); }, [year]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await api.put(`/holidays/${editId}`, form);
        toast.success('Holiday updated');
      } else {
        await api.post('/holidays', form);
        toast.success('Holiday added');
      }
      setForm({ name: '', date: '', type: 'Company', description: '' });
      setEditId(null);
      setShowForm(false);
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (h) => {
    setForm({
      name: h.name,
      date: new Date(h.date).toISOString().split('T')[0],
      type: h.type || 'Company',
      description: h.description || '',
    });
    setEditId(h._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this holiday?')) return;
    try {
      await api.delete(`/holidays/${id}`);
      toast.success('Holiday deleted');
      fetchHolidays();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  const resetForm = () => {
    setForm({ name: '', date: '', type: 'Company', description: '' });
    setEditId(null);
    setShowForm(!showForm);
  };

  return (
    <div>
      <div className="page-header">
        <h1>Holidays</h1>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[year - 1, year, year + 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button className="btn-secondary" onClick={fetchHolidays} title="Refresh"><FiRefreshCw /></button>
          {isAdminHR && (
            <button className="btn-primary" onClick={resetForm}>
              <FiPlus /> {showForm ? 'Cancel' : 'Add Holiday'}
            </button>
          )}
        </div>
      </div>

      {showForm && isAdminHR && (
        <div className="form-card" style={{ marginTop: '1rem' }}>
          <h3>{editId ? 'Edit' : 'Add'} Holiday</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label>Holiday Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Date</label>
                <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  {HOLIDAY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional" />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }}>
              {editId ? 'Update' : 'Add'} Holiday
            </button>
          </form>
        </div>
      )}

      {loading ? <div className="loading">Loading...</div> : (
        <table className="table" style={{ marginTop: '1rem' }}>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Holiday Name</th>
              <th>Date</th>
              <th>Day</th>
              <th>Type</th>
              <th>Description</th>
              {isAdminHR && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {holidays.map((h, index) => (
              <tr key={h._id}>
                <td>{index + 1}</td>
                <td><strong>{h.name}</strong></td>
                <td>{new Date(h.date).toLocaleDateString()}</td>
                <td>{new Date(h.date).toLocaleDateString('en-US', { weekday: 'long' })}</td>
                <td><span className={`status-badge status-${h.type?.toLowerCase()}`}>{h.type}</span></td>
                <td>{h.description || '-'}</td>
                {isAdminHR && (
                  <td className="actions">
                    <button className="btn-sm btn-warning" onClick={() => handleEdit(h)}><FiEdit /></button>
                    <button className="btn-sm btn-danger" onClick={() => handleDelete(h._id)}><FiTrash2 /></button>
                  </td>
                )}
              </tr>
            ))}
            {holidays.length === 0 && (
              <tr><td colSpan={isAdminHR ? 7 : 6} style={{ textAlign: 'center' }}>No holidays found for {year}</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default HolidayList;
